import { useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * A run of "Schritt 1 … n" sections shown one step at a time: the step's
 * name on a badge above a card with its picture and text, arrows on both
 * sides and the step numbers underneath (see utils/stepGroups.js).
 *
 * `renderMarkdown` renders a step's Markdown with the article's own
 * components, so pictures, links, video and audio look as everywhere else.
 */
function StepCarousel({ steps, renderMarkdown }) {
  const { t } = useTranslation();
  const [index, setIndex] = useState(0);
  const step = steps[index];
  const last = steps.length - 1;

  function go(next) {
    setIndex(Math.max(0, Math.min(last, next)));
  }

  function handleKeyDown(event) {
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      go(index - 1);
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      go(index + 1);
    }
  }

  return (
    <section
      className="step-carousel"
      aria-roledescription={t('stepCarousel.roleDescription')}
      aria-label={t('stepCarousel.label', { count: steps.length })}
      tabIndex={0}
      onKeyDown={handleKeyDown}
    >
      <div className="step-carousel__stage">
        <button
          type="button"
          className="step-carousel__arrow step-carousel__arrow--prev"
          onClick={() => go(index - 1)}
          disabled={index === 0}
          aria-label={t('stepCarousel.previous')}
        >
          <i className="fa-solid fa-chevron-left" aria-hidden="true" />
        </button>

        <div
          className="step-carousel__card"
          role="group"
          aria-roledescription={t('stepCarousel.slide')}
          aria-label={t('stepCarousel.position', { current: index + 1, total: steps.length })}
        >
          <p className="step-carousel__badge">{step.heading}</p>

          <div className="step-carousel__content" aria-live="polite">
            {renderMarkdown(step.text)}
          </div>
        </div>

        <button
          type="button"
          className="step-carousel__arrow step-carousel__arrow--next"
          onClick={() => go(index + 1)}
          disabled={index === last}
          aria-label={t('stepCarousel.next')}
        >
          <i className="fa-solid fa-chevron-right" aria-hidden="true" />
        </button>
      </div>

      <nav className="step-carousel__pages" aria-label={t('stepCarousel.pages')}>
        {steps.map((item, i) => (
          <button
            key={`${item.number}-${i}`}
            type="button"
            className={`step-carousel__page${i === index ? ' is-active' : ''}`}
            onClick={() => go(i)}
            aria-current={i === index ? 'step' : undefined}
            aria-label={item.heading}
          >
            {item.number}
          </button>
        ))}
      </nav>
    </section>
  );
}

export default StepCarousel;
